import React, { useEffect } from "react";
import PropTypes from "prop-types";
import Select from "components/shared/Select";
import ChatArea from "components/ai_tools/ChatArea";
import { useFormik } from "formik";
import * as Yup from "yup";
import pluralize from "pluralize";
import { ACCESS_TIERS } from "constants";
import { AccessTierProvider } from "contexts/AccessTierContext";
import AIToolsLayout from "./layout/AIToolsLayout";
import {
  AI_TOOL_CHANNELS,
  AI_TOOL_NAMES,
  AI_TOOLS_LABEL_MAP,
  AI_TOOLS_LOADING_DEFAULTS,
  REUSABLES
} from "./constants";
import useChatMessages from "./hooks/useChatMessages";
import AIFormLayout from "./shared/AIForm/AIFormLayout";
import useFormDisableFields from "./hooks/useFormDisableFields";
import useWindowResizeAndFormCollapse from "./hooks/useWindowResizeAndFormCollapse";
import useDiscussionQuestionsFormPopulator from "./hooks/useDiscussionQuestionsFormPopulator";

const ContextualizeDiscussionQuestions = (props) => {
  return (
    <AccessTierProvider accessTier={props.accessTier}>
      <DiscussionQuestions {...props} />
    </AccessTierProvider>
  );
};

const DiscussionQuestions = (props) => {
  const {
    aiAnswersCompletePath,
    infiniteAnswers,
    locked,
    options,
    quotaRemaining: quotaFromProps,
    signupPath,
    submissionPath,
    upgradeable
  } = props;

  const {
    topics,
    loadingTopics,
    selectedTopic,
    setSelectedTopic,
    selectedSubtopic,
    setSelectedSubtopic,
    fetchDropdownContent
  } = useDiscussionQuestionsFormPopulator(formik);

  const validationSchema = Yup.object().shape({
    subtopic: Yup.string().when("topic", (_) => {
      if (selectedTopic?.data) {
        return Yup.string().required(
          `Please select your ${
            AI_TOOLS_LABEL_MAP[selectedTopic.label] ??
            selectedTopic.labelText?.toLowerCase()
          }`
        );
      }
    }),
    title: Yup.string().required("Please select your title"),
    topic: Yup.string().when("title", (title) => {
      if (title) {
        return Yup.string().required("Please select your topic");
      }
    })
  });

  const { isLessThanSmallBreakpoint, collapsed, setCollapsed } =
    useWindowResizeAndFormCollapse();

  const formik = useFormik({
    initialValues: {
      subtopic: "",
      title: prevSlug || "",
      topic: ""
    },
    onSubmit: (values) => {
      validationSchema
        .validate(values, { abortEarly: false })
        .then(() => {
          handleQuizDiscussionEssayPrompt(values);
          if (isLessThanSmallBreakpoint) {
            setCollapsed(true);
          }
        })
        .catch((errors) => {
          formik.setErrors(errors);
        });
    },
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema
  });

  const {
    isStreaming,
    quotaRemaining,
    messages,
    prevSlug,
    handleQuizDiscussionEssayPrompt,
    fetchAiAnswers,
    canFetchMoreAnswers,
    loadingPastMessages
  } = useChatMessages({
    aiAnswersCompletePath,
    channel: AI_TOOL_CHANNELS.DISCUSSION_QUESTIONS,
    eventCategory: "Discussion Questions Generator",
    formik,
    infiniteAnswers,
    locked,
    messageText: selectedSubtopic?.messageText ?? selectedTopic?.messageText,
    quotaFromProps,
    signupPath,
    submissionPath,
    toolName: AI_TOOL_NAMES.DISCUSSION_QUESTIONS,
    upgradeable
  });

  const { disableFields } = useFormDisableFields(
    locked,
    quotaRemaining,
    infiniteAnswers
  );

  useEffect(() => {
    formik.setFieldValue("title", prevSlug);
  }, [prevSlug]);

  return (
    <AIToolsLayout dataTestId="DiscussionQuestions">
      <AIFormLayout
        {...props}
        collapsed={collapsed}
        isLessThanSmallBreakpoint={isLessThanSmallBreakpoint}
        setCollapsed={setCollapsed}
        toolName={AI_TOOL_NAMES.DISCUSSION_QUESTIONS}
        loading={isStreaming || loadingPastMessages}
        storageKey="discussionQuestions"
        quotaRemaining={quotaRemaining}
        formik={formik}
      >
        <Select
          errorMessage={formik.errors.title}
          id={REUSABLES.TITLE}
          isDisabled={isStreaming || disableFields || loadingPastMessages}
          isSearchable
          labelText="Title"
          name={REUSABLES.TITLE}
          options={options}
          placeholder={!disableFields ? "e.g. Hamlet (Shakespeare)" : ""}
          onChange={(e) => {
            formik.setFieldValue(REUSABLES.TITLE, e.value);
            formik.setFieldValue(REUSABLES.TOPIC, undefined);
            formik.setFieldValue(REUSABLES.SUBTOPIC, undefined);
            formik.setFieldError(REUSABLES.TOPIC, undefined);
            formik.setFieldError(REUSABLES.SUBTOPIC, undefined);
            fetchDropdownContent(e.guide_path);
          }}
          value={formik.values.title}
        />
        {topics && !disableFields && (
          <Select
            errorMessage={
              loadingTopics && formik.errors.topic
                ? "Loading, select one when complete"
                : formik.errors.topic
            }
            id={REUSABLES.TOPIC}
            isDisabled={
              loadingTopics ||
              isStreaming ||
              loadingPastMessages ||
              disableFields
            }
            isSearchable
            isControlled
            isLoading={loadingTopics}
            labelText="Topic"
            name={REUSABLES.TOPIC}
            options={loadingTopics ? [AI_TOOLS_LOADING_DEFAULTS] : topics}
            placeholder={!disableFields ? "e.g. Themes" : ""}
            onChange={(e) => {
              formik.setFieldValue(REUSABLES.TOPIC, e.value);
              formik.setFieldValue(REUSABLES.SUBTOPIC, e.subtopic ?? undefined);
              formik.setFieldError(REUSABLES.SUBTOPIC, undefined);

              setSelectedTopic(topics.find((topic) => topic.value === e.value));
              setSelectedSubtopic(null);
            }}
            value={
              loadingTopics
                ? AI_TOOLS_LOADING_DEFAULTS.value
                : formik.values.topic
            }
          />
        )}
        {selectedTopic?.data && !disableFields && (
          <Select
            errorMessage={formik.errors.subtopic}
            id={REUSABLES.SUBTOPIC}
            isDisabled={isStreaming || disableFields || loadingPastMessages}
            isSearchable
            isControlled
            labelText={
              selectedTopic.labelText ??
              pluralize(formik.values.topic, 1, false)
            }
            name={REUSABLES.SUBTOPIC}
            options={selectedTopic.data}
            placeholder={!disableFields ? "Select one" : ""}
            onChange={(e) => {
              formik.setFieldValue(REUSABLES.SUBTOPIC, e.value);
              setSelectedSubtopic(
                selectedTopic.data.find((topic) => topic.value === e.value)
              );
            }}
            value={formik.values.subtopic}
          />
        )}
      </AIFormLayout>
      <ChatArea
        canLoadMore={canFetchMoreAnswers}
        isStreaming={isStreaming}
        loadingPastMessages={loadingPastMessages}
        loadMore={fetchAiAnswers}
        messages={messages}
      />
    </AIToolsLayout>
  );
};

DiscussionQuestions.propTypes = {
  accessTier: PropTypes.oneOf(Object.values(ACCESS_TIERS)).isRequired,
  aiAnswersCompletePath: PropTypes.string,
  infiniteAnswers: PropTypes.bool,
  locked: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.string
    })
  ),
  questionMinimum: PropTypes.number,
  quotaRemaining: PropTypes.number.isRequired,
  resetDate: PropTypes.string,
  signupPath: PropTypes.string,
  submissionPath: PropTypes.string,
  upgradeable: PropTypes.bool
};

DiscussionQuestions.defaultProps = {
  aiAnswersCompletePath: "/ai_answers/complete",
  locked: false,
  options: [],
  questionMinimum: 25,
  resetDate: "",
  signupPath: "/sign-up?focus=teacher",
  submissionPath: "/ai/discussion-question-generator",
  upgradeable: false
};

export default ContextualizeDiscussionQuestions;
